import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CatalogService, InventoryQcInspection, QcWorkbenchSummary } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-qc-inspections',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.qc.inspections.title')" [description]="t('catalog.qc.inspections.description')">
      <ain-inventory-page-hint pageKey="qc" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        @if (workbench()) {
          <section class="workbench">
            <h4>{{ t('catalog.qc.workbench.title') }}</h4>
            <div class="stats">
              <article><strong>{{ t('catalog.controlRoom.qcPending') }}</strong><span>{{ workbench()!.pendingCount }}</span></article>
              <article><strong>{{ t('catalog.controlRoom.qcInProgress') }}</strong><span>{{ workbench()!.inProgressCount }}</span></article>
              <article class="warn"><strong>{{ t('catalog.controlRoom.qcFailed') }}</strong><span>{{ workbench()!.failedCount }}</span></article>
              <article><strong>{{ t('catalog.controlRoom.qcPassed') }}</strong><span>{{ workbench()!.passedCount }}</span></article>
            </div>
          </section>
        }

        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('catalog.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelectedDetail()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>

          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('catalog.filter.hide') : t('catalog.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
          </div>

          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('catalog.tracking.status') }}</span>
                <p-select [options]="statusOptions" [(ngModel)]="filters.status" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" (ngModelChange)="load()" />
              </label>
              <label>
                <span>{{ t('catalog.qc.inspectionType') }}</span>
                <p-select [options]="inspectionTypeOptions" [(ngModel)]="filters.inspectionType" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" (ngModelChange)="load()" />
              </label>
            </div>
          }

          <p-table
            [value]="rows()"
            dataKey="id"
            [paginator]="true"
            [rows]="15"
            [selection]="selectedRows()"
            (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('catalog.qc.referenceNumber') }}</th>
                <th>{{ t('catalog.qc.inspectionType') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.qc.failReason') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.referenceNumber }}</button></td>
                <td>{{ row.inspectionType }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.failReasonCode || '-' }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .workbench { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .workbench h4 { margin: 0 0 8px; }
    .stats { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); }
    .stats article { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; display: grid; gap: 4px; }
    .stats article.warn { border-color: #fca5a5; background: #fef2f2; }
    .stats strong { font-size: 11px; color: #64748b; }
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; justify-content: flex-start; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .filters span { font-size: 12px; color: #475569; font-weight: 600; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class InventoryQcInspectionsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly showFilters = signal(true);
  readonly rows = signal<InventoryQcInspection[]>([]);
  readonly workbench = signal<QcWorkbenchSummary | null>(null);
  readonly selectedRows = signal<InventoryQcInspection[]>([]);
  readonly filters = { status: '', inspectionType: '' };

  readonly statusOptions = [
    { label: '—', value: '' },
    { label: 'Pending', value: 'Pending' },
    { label: 'InProgress', value: 'InProgress' },
    { label: 'Passed', value: 'Passed' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  readonly inspectionTypeOptions = [
    { label: '—', value: '' },
    { label: 'Inbound', value: 'Inbound' },
    { label: 'Outbound', value: 'Outbound' }
  ];

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.load();
    this.service.getOperationsControlRoom().subscribe({
      next: summary => this.workbench.set(summary.quality),
      error: () => this.workbench.set(null)
    });
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.service.listQcInspections(this.filters.status || undefined, this.filters.inspectionType || undefined).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.error.set(this.t('catalog.qc.inspections.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  onSelectionChange(rows: InventoryQcInspection[]): void {
    this.selectedRows.set(rows);
  }

  openCreate(): void {
    void this.router.navigate(['/inventory/qc-inspections', 'new']);
  }

  openSelectedDetail(): void {
    const selected = this.selectedRows()[0];
    if (selected) void this.router.navigate(['/inventory/qc-inspections', selected.id]);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/inventory/qc-inspections', id]);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
